<template>
    <BillItem
        class="unpaid-line-item"
        :class="{ disabled: disabled }"
        ref="lineItemComponent"
        :item="item"
        :inactive="inactive"
        :quantityDigits="quantityDigits"
        :currentQuantity="currentMultiplier"
        :totalQuantity="unpaidQuantity"
        :showInternalNames="props.showInternalNames"
        :showComment="true"
        :allowDiscountRemoval="true"
        :showOpenOrderDetailsButton="props.isUserWaiterOrAdmin"
        :forceDisplayGroupName="props.forceDisplayGroupName"
        @click="onClick"
        :onClickOpenOrderDetails="onClickOpenOrderDetails"
    >
        <template #prepend-header>
            <QuantitySelectionCheckbox
                v-if="isSelectable"
                :selectedQuantity="selectedQuantity"
                :totalQuantity="unpaidQuantity"
                :visible="true"
            />
        </template>
    </BillItem>
</template>

<script setup lang="ts">
    import { UnpaidLineItem } from "@/data/model/Bill";
    import { Ripple } from "@/shared/ui/Ripple";
    import { computed, onMounted, ref, watch } from "vue";

    import QuantitySelectionCheckbox from "@/components/elements/QuantitySelectionCheckbox.vue";
    import BillItem from "@/components/order/BillItem.vue";

    // define properties
    const props = withDefaults(
        defineProps<{
            item: UnpaidLineItem;
            isSelectionMode: boolean;
            disabled?: boolean;
            quantityDigits?: number;
            showInternalNames: boolean;
            forceDisplayGroupName?: boolean;
            isUserWaiterOrAdmin: boolean;
            isSelectionEnabled?: boolean;
        }>(),
        {
            isSelectionEnabled: true,
        }
    );

    // define emits
    const emit = defineEmits(["selectionChange", "onClickOpenOrderDetails"]);

    // define reactive
    const selectedQuantity = ref(props.item.selectedQuantity);
    const unpaidQuantity = ref(props.item.unpaidQuantity);

    // define methods
    function onClick() {
        if (!isSelectable.value) return;

        selectedQuantity.value++;
        if (selectedQuantity.value > unpaidQuantity.value) {
            selectedQuantity.value = 0;
        }

        emit("selectionChange", {
            item: props.item,
            selectedQuantity: selectedQuantity.value,
        });
    }

    function onClickOpenOrderDetails() {
        if (props.disabled) return;

        emit("onClickOpenOrderDetails", props.item.orderId);
    }

    // define computed
    const isSelectable = computed(() => !props.disabled && props.isSelectionEnabled);
    const inactive = computed(() => props.isSelectionMode && selectedQuantity.value == 0);
    const currentMultiplier = computed(() =>
        selectedQuantity.value > 0 ? selectedQuantity.value : unpaidQuantity.value
    );

    // define html refs
    const lineItemComponent = ref<InstanceType<typeof BillItem> | null>(null);

    // define watch
    watch(isSelectable, (selectable) => {
        if (selectable) {
            Ripple.bindRippleOut($(lineItemComponent.value?.$el as any), undefined, 0.18);
        } else {
            Ripple.unbind($(lineItemComponent.value?.$el as any));
        }
    });

    watch(props.item, () => {
        selectedQuantity.value = props.item.selectedQuantity;
        unpaidQuantity.value = props.item.unpaidQuantity;
    });

    watch(
        () => props.item.selectedQuantity,
        () => {
            selectedQuantity.value = props.item.selectedQuantity;
        }
    );

    watch(
        () => props.item.unpaidQuantity,
        () => {
            unpaidQuantity.value = props.item.unpaidQuantity;
        }
    );

    // define onMounted
    onMounted(() => {
        if (isSelectable.value) {
            Ripple.bindRippleOut($(lineItemComponent.value?.$el as any), undefined, 0.18);
        }
    });
</script>

<style lang="scss">
    .unpaid-line-item {
        -webkit-tap-highlight-color: transparent;
        &:not(.disabled) {
            cursor: pointer;
        }
    }
</style>
